import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Loader2, Sparkles } from 'lucide-react';
import { API } from '../App';
import { ASSETS } from '../constants/site';

async function streamSSE(url, body, onDelta) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Assistant is unavailable right now.');
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith('data:')) continue;
      const payload = line.slice(5).trim();
      if (payload === '[DONE]') return;
      try {
        const parsed = JSON.parse(payload);
        if (parsed.delta) onDelta(parsed.delta);
      } catch { /* partial json */ }
    }
  }
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hey — I'm the GSLAB assistant. Ask me anything about our websites, pricing or automations." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(localStorage.getItem('gslab_chat_session') || '');
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }, { role: 'assistant', text: '' }]);
    setBusy(true);
    try {
      await streamSSE(`${API}/ai/chat`, { session_id: sessionRef.current, message: text }, (delta) => {
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: 'assistant', text: next[next.length - 1].text + delta };
          return next;
        });
      });
    } catch (err) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: 'assistant', text: err.message };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-button"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, type: 'spring', stiffness: 200, damping: 16 }}
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F3C64F] text-[#050B14] flex items-center justify-center shadow-[0_8px_30px_rgba(212,175,55,0.4)] hover:shadow-[0_8px_40px_rgba(212,175,55,0.6)] transition-shadow duration-300"
        aria-label="Chat with the GSLAB assistant"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] max-h-[70vh] flex flex-col rounded-2xl border border-amber-500/25 bg-[#0B1528]/95 backdrop-blur-xl shadow-[0_24px_80px_rgba(0,0,0,0.6)] overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-800 bg-[#0F1C33]/80">
              <img src={ASSETS.icon} alt="" className="h-8 w-8 rounded-lg" />
              <div>
                <div className="font-display font-semibold text-sm flex items-center gap-2">
                  GSLAB Assistant <Sparkles size={12} className="text-amber-400" />
                </div>
                <div className="font-mono-tech text-[9px] tracking-[0.2em] text-emerald-400 uppercase">Online · replies instantly</div>
              </div>
            </div>

            <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[200px]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-medium'
                      : 'bg-[#0F1C33] border border-slate-800 text-slate-200'
                  }`}>
                    {m.text || (busy && i === messages.length - 1 ? '…' : '')}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={send} className="flex gap-2 p-3 border-t border-slate-800 bg-[#0F1C33]/60">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about pricing, timelines…"
                className="flex-1 bg-[#050B14]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-slate-600"
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={busy || !input.trim()}
                className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#F3C64F] text-[#050B14] flex items-center justify-center disabled:opacity-40 transition-opacity"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
